import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, X } from "lucide-react";
import { config } from "../config";
import { useCart } from "../context/CartContext";
import { useQuiz } from "../context/QuizContext";

export const Banner = () => {
  const [show, setShow] = useState(true);
  const [idx, setIdx] = useState(0);
  const messages = config.banner.messages || [];
  React.useEffect(() => {
    if (messages.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % messages.length), 3800);
    return () => clearInterval(t);
  }, [messages.length]);
  if (!config.banner.enabled || !show || messages.length === 0) return null;
  return (
    <div style={{ background: "var(--accent)", color: "var(--on-accent)" }} data-testid="top-banner">
      <div className="wrap" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "13px 20px", position: "relative", minHeight: 44, overflow: "hidden" }}>
        <AnimatePresence mode="wait">
          <motion.p
            key={idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            data-testid="banner-message"
            className="display"
            style={{ margin: 0, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", textAlign: "center", textTransform: "uppercase" }}
          >
            {messages[idx]}
          </motion.p>
        </AnimatePresence>
        <button data-testid="banner-close" onClick={() => setShow(false)}
          style={{ position: "absolute", right: 16, background: "none", border: "none", color: "var(--on-accent)", cursor: "pointer" }} aria-label="Fermer">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export const Header = () => {
  const { count, setOpen } = useCart();
  const { openQuiz } = useQuiz();
  const loc = useLocation();
  return (
    <header style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(10,10,10,0.82)", backdropFilter: "blur(16px)", borderBottom: "1px solid var(--border)" }}>
      <div className="wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 }}>
        <Link to="/" data-testid="logo-link" className="display" style={{ fontWeight: 700, fontSize: 24, letterSpacing: "0.08em" }}>CRUSH</Link>
        <nav style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <Link to="/boutique" data-testid="nav-boutique" style={{ fontSize: 14, color: loc.pathname === "/boutique" ? "var(--accent-light)" : "var(--text)" }}>Boutique</Link>
          <Link to="/science" data-testid="nav-science" style={{ fontSize: 14, color: loc.pathname === "/science" ? "var(--accent-light)" : "var(--text)" }}>La science</Link>
          <button onClick={openQuiz} data-testid="nav-quiz" style={{ background: "none", border: "1px solid var(--accent-light)", color: "var(--accent-light)", fontSize: 12, letterSpacing: "0.06em", textTransform: "uppercase", padding: "8px 14px", borderRadius: 4, cursor: "pointer", fontFamily: "var(--display)", fontWeight: 500 }}>Faire le test</button>
          <button data-testid="cart-button" onClick={() => setOpen(true)} aria-label="Panier"
            style={{ position: "relative", background: "none", border: "none", color: "var(--text)", cursor: "pointer", display: "flex", alignItems: "center" }}>
            <ShoppingBag size={22} />
            {count > 0 && (
              <span data-testid="cart-count" style={{ position: "absolute", top: -8, right: -10, background: "var(--accent)", color: "var(--on-accent)", fontSize: 11, fontWeight: 700, minWidth: 18, height: 18, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 5px" }}>{count}</span>
            )}
          </button>
        </nav>
      </div>
    </header>
  );
};

export const Footer = () => (
  <footer style={{ borderTop: "1px solid var(--border)", background: "var(--surface)" }} data-testid="footer">
    <div className="wrap" style={{ padding: "48px 20px" }}>
      <div style={{ display: "grid", gap: 32, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
        <div>
          <div className="display" style={{ fontWeight: 700, fontSize: 22, letterSpacing: "0.08em", marginBottom: 12 }}>CRUSH</div>
          <p style={{ color: "var(--text-muted)", fontSize: 13, margin: 0 }}>{config.slogan || "CRAQUEZ POUR CRUSH."}</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 14 }}>
          <span className="eyebrow" style={{ color: "var(--text-muted)", marginBottom: 6 }}>Informations</span>
          <Link to="/cgv" data-testid="footer-cgv" className="link-accent">CGV</Link>
          <Link to="/mentions-legales" className="link-accent">Mentions légales</Link>
          <Link to="/confidentialite" className="link-accent">Confidentialité</Link>
          <Link to="/livraison-retours" className="link-accent">Livraison & retours</Link>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 14 }}>
          <span className="eyebrow" style={{ color: "var(--text-muted)", marginBottom: 6 }}>Contact</span>
          <a href={`mailto:${config.brand.contact_email}`} className="link-accent">{config.brand.contact_email}</a>
          <a href={config.brand.instagram || "#"} className="link-accent">Instagram</a>
          <a href={config.brand.tiktok || "#"} className="link-accent">TikTok</a>
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
          <span className="eyebrow" style={{ display: "block", marginBottom: 10 }}>Paiement sécurisé</span>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {["CB", "Visa", "Mastercard", "Apple Pay", "Google Pay", "PayPal"].map((m) => (
              <span key={m} style={{ border: "1px solid var(--border)", borderRadius: 4, padding: "4px 8px", fontSize: 11 }}>{m}</span>
            ))}
          </div>
        </div>
      </div>
      <div style={{ marginTop: 40, paddingTop: 20, borderTop: "1px solid var(--border)", fontSize: 12, color: "var(--text-muted)" }}>© CRUSH 2026</div>
    </div>
  </footer>
);

export const CookieConsent = () => {
  const [choice, setChoice] = useState(() => localStorage.getItem("crush_consent"));
  if (choice) return null;
  const decide = (v) => { localStorage.setItem("crush_consent", v); setChoice(v); };
  return (
    <div data-testid="cookie-consent" style={{ position: "fixed", bottom: 16, left: 16, right: 16, zIndex: 80, maxWidth: 520, margin: "0 auto" }}>
      <div className="card" style={{ padding: 20 }}>
        <p style={{ margin: "0 0 14px", fontSize: 14, color: "var(--text-muted)" }}>
          Nous utilisons des cookies de mesure d'audience uniquement après votre consentement. Aucune donnée n'est collectée avant votre choix.
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn btn-primary" style={{ flex: 1, minWidth: 120, padding: "12px 20px" }} data-testid="consent-accept" onClick={() => decide("accepted")}>Accepter</button>
          <button className="btn btn-secondary" style={{ flex: 1, minWidth: 120, padding: "11px 20px" }} data-testid="consent-refuse" onClick={() => decide("refused")}>Refuser</button>
        </div>
      </div>
    </div>
  );
};
